/**
 * Ranks the user's top grammar error tags as horizontal bars. Tells the
 * learner "you mix up article gender most often" without needing to
 * understand the rubric internals.
 */
import type { GrammarErrorRank } from "../../api";

// Friendlier labels for the raw snake_case tags the signal extractor
// emits. Fall through to title-casing the tag if no mapping exists.
const TAG_LABELS: Record<string, string> = {
  article_gender: "Wrong article gender",
  article_case: "Wrong article case",
  missing_article: "Missing article",
  misspelled_article: "Misspelled article",
  article_missing: "Missing article",
  article_spelling: "Misspelled article",
  case_dative: "Dative case",
  case_accusative: "Accusative case",
  case_genitive: "Genitive case",
  verb_conjugation: "Verb conjugation",
  verb_form: "Verb form",
  verb_form_missing: "Missing verb form",
  word_order: "Word order",
  adjective_ending: "Adjective ending",
  preposition_usage: "Preposition choice",
  spelling_capitalization: "Noun capitalization",
  past_participle_formation: "Past participle",
  // Soft tags — tracked but don't penalize mastery
  missing_capitalization: "Missing capitalization",
  mixed_languages: "Code-switching",
  spelling: "Spelling",
  wrong_pronoun: "Wrong pronoun",
  tense: "Wrong tense",
  plural_form: "Plural form",
  separable_prefix: "Separable prefix",
};

function humanLabel(tag: string): string {
  if (TAG_LABELS[tag]) return TAG_LABELS[tag];
  // Fallback: "some_tag" → "Some tag"
  const spaced = tag.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function GrammarErrorRanking({
  errors,
  mastery,
}: {
  errors: GrammarErrorRank[];
  mastery: number;
}) {
  if (!errors || errors.length === 0) {
    return (
      <div className="card">
        <div className="mb-3 flex items-baseline justify-between">
          <div>
            <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
              grammar
            </div>
            <div className="text-[11px] text-ink-faint mt-0.5">
              recent error patterns
            </div>
          </div>
          <div className="font-serif text-3xl text-ink">
            {Math.round(mastery * 100)}
            <span className="text-base text-ink-faint">%</span>
          </div>
        </div>
        <div className="text-ink-faint text-sm py-4 text-center">
          no grammar errors recorded yet — nice!
        </div>
      </div>
    );
  }
  const max = errors[0].count;

  return (
    <div className="card">
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
            grammar
          </div>
          <div className="text-[11px] text-ink-faint mt-0.5">
            your top error patterns — last 60 days
          </div>
        </div>
        <div>
          <div className="font-serif text-3xl text-ink">
            {Math.round(mastery * 100)}
            <span className="text-base text-ink-faint">%</span>
          </div>
          <div className="text-[10px] text-ink-faint text-right">
            overall mastery
          </div>
        </div>
      </div>
      <div className="space-y-2 mt-4">
        {errors.map((e) => {
          const pct = (e.count / max) * 100;
          const isSoft = e.kind === "soft";
          return (
            <div key={e.tag} className="flex items-center gap-3 text-xs">
              <span className="w-40 shrink-0 text-ink-soft text-right flex items-center justify-end gap-1.5">
                <span>{humanLabel(e.tag)}</span>
                {isSoft ? (
                  <span
                    className="text-[8px] font-medium uppercase tracking-wider text-ink-faint bg-ink-faint/10 px-1 py-[1px] rounded"
                    title="Style / typing convention — tracked but doesn't penalize mastery"
                  >
                    soft
                  </span>
                ) : null}
              </span>
              <div className="flex-1 h-3.5 bg-ink-faint/10 rounded-sm overflow-hidden">
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: `${Math.max(3, pct)}%`,
                    // Hard = terracotta (penalizes mastery).
                    // Soft = amber (tracked, doesn't penalize).
                    background: isSoft ? "#c88649" : "#B8472C",
                  }}
                />
              </div>
              <span className="w-12 shrink-0 text-right text-ink font-medium">
                {e.count}×
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-ink-soft leading-relaxed">
        <span className="text-terracotta font-medium">Hard</span> tags
        penalize your mastery score.{" "}
        <span style={{ color: "#c88649" }} className="font-medium">
          Soft
        </span>{" "}
        tags (capitalization, code-switching, spelling) are tracked for
        transparency but don't block CEFR promotion. The planner
        prioritizes fixing your most-common hard errors first.
      </p>
    </div>
  );
}
